import type { AxiosResponse } from 'axios';
import { getMarketingApi } from '@/data/marketingApi';
import type { MarketingJobData, MarketingMeta } from '@/data/marketingApiTypes';

/**
 * HTTP helpers for the Growth / digital-marketing backend (`marketing-api.elva.ai`,
 * mounted at `/v1/marketing/`). Uses a dedicated marketing client — platform `getMainApi()`
 * stays on core-api-az for locations and other org endpoints.
 *
 * Clinic scope: bearer token + `location_id` query param (injected in `marketingApi.ts`
 * from the selected workspace). React Query hooks gate fetches until a location is selected.
 */

export const MARKETING_BASE = '/v1/marketing';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export type PaginationMeta = MarketingMeta;

export interface ApiEnvelope<T> {
  data: T;
  meta?: PaginationMeta;
}

export interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
}

function cleanParams(params?: QueryParams): Record<string, string | number | boolean> | undefined {
  if (!params) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

function marketingPath(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${MARKETING_BASE}${normalized}`;
}

export function unwrapData<T>(res: AxiosResponse<ApiEnvelope<T> | T>): T {
  const body = res.data;
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as ApiEnvelope<T>).data;
  }
  return body as T;
}

export function unwrapMeta(res: AxiosResponse<ApiEnvelope<unknown>>): PaginationMeta | undefined {
  const body = res.data;
  if (body && typeof body === 'object' && 'meta' in body) {
    return (body as ApiEnvelope<unknown>).meta;
  }
  return undefined;
}

export async function growthGet<T = unknown>(path: string, params?: QueryParams): Promise<T> {
  const res = await getMarketingApi().get<ApiEnvelope<T> | T>(marketingPath(path), {
    params: cleanParams(params),
  });
  return unwrapData(res);
}

/** GET endpoints that may return 200 (data) or 202 (job queued while generating). */
export async function growthGetAccepted<T = unknown>(
  path: string,
  params?: QueryParams
): Promise<{ data: T; status: number }> {
  const res = await getMarketingApi().get<ApiEnvelope<T>>(marketingPath(path), {
    params: cleanParams(params),
    validateStatus: (status) => status === 200 || status === 201 || status === 202,
  });
  return { data: unwrapData(res), status: res.status };
}

export async function growthGetWithMeta<T = unknown>(
  path: string,
  params?: QueryParams
): Promise<{ data: T; meta?: PaginationMeta }> {
  const res = await getMarketingApi().get<ApiEnvelope<T>>(marketingPath(path), {
    params: cleanParams(params),
  });
  const meta = unwrapMeta(res as AxiosResponse<ApiEnvelope<unknown>>);
  return { data: unwrapData(res), ...(meta ? { meta } : {}) };
}

export async function growthList<T = unknown>(
  path: string,
  params?: QueryParams
): Promise<{ items: T[]; meta?: PaginationMeta }> {
  const { data, meta } = await growthGetWithMeta<
    T[] | { results?: T[]; items?: T[]; meta?: PaginationMeta }
  >(path, params);
  if (Array.isArray(data)) return meta ? { items: data, meta } : { items: data };
  if (data && typeof data === 'object') {
    const row = data as { results?: T[]; items?: T[]; meta?: PaginationMeta };
    const items = row.results ?? row.items ?? [];
    const listMeta = row.meta ?? meta;
    return listMeta ? { items, meta: listMeta } : { items };
  }
  return { items: [] };
}

export async function growthPost<T = unknown>(
  path: string,
  body?: unknown,
  params?: QueryParams
): Promise<T> {
  const res = await getMarketingApi().post<ApiEnvelope<T> | T>(marketingPath(path), body ?? {}, {
    params: cleanParams(params),
  });
  return unwrapData(res);
}

/** Multipart POST — lets axios set the boundary (do not force application/json). */
export async function growthPostFormData<T = unknown>(
  path: string,
  formData: FormData,
  params?: QueryParams
): Promise<T> {
  const res = await getMarketingApi().post<ApiEnvelope<T> | T>(marketingPath(path), formData, {
    params: cleanParams(params),
    transformRequest: [
      (data, headers) => {
        if (headers && typeof headers === 'object') {
          delete (headers as Record<string, unknown>)['Content-Type'];
        }
        return data;
      },
    ],
  });
  return unwrapData(res);
}

/** POST endpoints that may return 200 (data) or 202 (accepted / job queued). */
export async function growthPostAccepted<T = unknown>(
  path: string,
  body?: unknown,
  params?: QueryParams
): Promise<{ data: T; status: number }> {
  const res = await getMarketingApi().post<ApiEnvelope<T>>(marketingPath(path), body ?? {}, {
    params: cleanParams(params),
    validateStatus: (status) => status === 200 || status === 201 || status === 202,
  });
  return { data: unwrapData(res), status: res.status };
}

/** Async writes that return 202 `{ data: { status: "queued", job_id } }`. */
export async function growthPostAsync(
  path: string,
  body?: unknown,
  params?: QueryParams
): Promise<MarketingJobData> {
  const res = await getMarketingApi().post<ApiEnvelope<MarketingJobData>>(
    marketingPath(path),
    body ?? {},
    {
      params: cleanParams(params),
      validateStatus: (status) => status === 200 || status === 201 || status === 202,
    }
  );
  return unwrapData(res);
}

export async function growthPatch<T = unknown>(
  path: string,
  body?: unknown,
  params?: QueryParams
): Promise<T> {
  const res = await getMarketingApi().patch<ApiEnvelope<T> | T>(marketingPath(path), body ?? {}, {
    params: cleanParams(params),
  });
  return unwrapData(res);
}

/** Budget PATCH validates an allowlist — location_id must not appear in the JSON body. */
const SETTINGS_SECTIONS_SKIP_BODY_LOCATION_ID = new Set(['budget']);

/** PATCH /settings/{section}/ — budget omits body location_id; other sections require it. */
export async function growthPatchSettingsSection(
  section: string,
  body: Record<string, unknown>,
  locationId: number
): Promise<void> {
  await getMarketingApi().patch(marketingPath(`/settings/${section}/`), body, {
    params: { location_id: locationId },
    skipLocationIdBody: SETTINGS_SECTIONS_SKIP_BODY_LOCATION_ID.has(section),
  });
}

export async function growthDelete<T = unknown>(path: string, params?: QueryParams): Promise<T> {
  const res = await getMarketingApi().delete<ApiEnvelope<T> | T>(marketingPath(path), {
    params: cleanParams(params),
  });
  return unwrapData(res);
}

export async function growthGetBlob(path: string, params?: QueryParams): Promise<Blob> {
  const res = await getMarketingApi().get<Blob>(marketingPath(path), {
    params: cleanParams(params),
    responseType: 'blob',
  });
  return res.data;
}

export async function growthPostBlob(
  path: string,
  body?: unknown,
  params?: QueryParams
): Promise<{ blob: Blob; status: number }> {
  const res = await getMarketingApi().post<Blob>(marketingPath(path), body ?? {}, {
    params: cleanParams(params),
    responseType: 'blob',
    validateStatus: (status) => status === 200 || status === 201 || status === 202,
  });
  return { blob: res.data, status: res.status };
}
